import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../app/appUtils";
import AppButton from "../../components/ui/AppButton";
import AppInputField from "../../components/ui/AppInputField";
import { Spinner } from "../../components/ui/common";
import { Modal } from "../../components/ui/Modal";
import SuccessView from "../../components/ui/SuccessView";
import { useToast } from "../../hooks/useToast";
import StaffDropdown, { StaffOption } from "../staff/ui/StaffDropdown";
import { Requisition } from "./@types";
import {
  useCreateRequisitionMutation,
  useUpdateRequisitionMutation,
} from "./requisitionApiSlice";

const itemSchema = z.object({
  description: z.string().nonempty("Item description is required"),
  quantity: z.number().min(1, "Quantity must be at least 1"),
  unitPrice: z.number().min(0.01, "Unit price must be greater than 0"),
});

const approverSchema = z.object({
  id: z.string().nonempty("Please select an approver"),
  name: z.string(),
  email: z.string().email(),
  level: z.number(),
});

export const requisitionFormSchema = z.object({
  cardId: z.string().nonempty("Card ID is required"),
  totalPayment: z.number().min(0.01, "Total payment must be greater than 0"),
  payTo: z.string().nonempty("Pay to is required"),
  bankName: z.string().nonempty("Bank name is required"),
  bankAccountNumber: z.string().nonempty("Bank account number is required"),
  description: z.string().nonempty("Description is required"),
  items: z.array(itemSchema).min(1, "At least one item is required"),
  approvers: z
    .array(approverSchema)
    .length(2, "Exactly two approvers are required"),
});

export type RequisitionFormData = z.infer<typeof requisitionFormSchema>;

interface RequisitionModalProps {
  isOpen: boolean;
  onClose: () => void;
  requisition?: Requisition | null;
  mode?: "add" | "edit";
  initialCardId?: string;
}

const RequisitionModal: React.FC<RequisitionModalProps> = ({
  isOpen,
  onClose,
  requisition = null,
  mode = requisition ? "edit" : "add",
  initialCardId,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const toast = useToast();
  const isEditMode = mode === "edit" && requisition;

  const [
    createRequisition,
    {
      isLoading: isCreating,
      isSuccess: createSuccess,
      isError: createError,
      error: createErrorData,
      reset: resetCreate,
    },
  ] = useCreateRequisitionMutation();

  const [
    updateRequisition,
    {
      isLoading: isUpdating,
      isSuccess: updateSuccess,
      isError: updateError,
      error: updateErrorData,
      reset: resetUpdate,
    },
  ] = useUpdateRequisitionMutation();

  const isLoading = isCreating || isUpdating;
  const isSuccess = createSuccess || updateSuccess;
  const isError = createError || updateError;
  const error = createErrorData || updateErrorData;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset,
  } = useForm<RequisitionFormData>({
    resolver: zodResolver(requisitionFormSchema),
    mode: "onChange",
    defaultValues: {
      cardId: "",
      totalPayment: 0,
      payTo: "",
      bankName: "",
      bankAccountNumber: "",
      description: "",
      items: [{ description: "", quantity: 1, unitPrice: 0 }],
      approvers: [
        { id: "", name: "", email: "", level: 1 },
        { id: "", name: "", email: "", level: 2 },
      ],
    },
  });

  const {
    fields: itemFields,
    append: appendItem,
    remove: removeItem,
  } = useFieldArray({
    control,
    name: "items",
  });

  const watchItems = watch("items");

  // Calculate total amount
  const totalAmount = watchItems.reduce((sum, item) => {
    const itemTotal = (item.quantity || 0) * (item.unitPrice || 0);
    return sum + itemTotal;
  }, 0);

  // Populate form when editing or when initialCardId is provided
  useEffect(() => {
    if (isEditMode && requisition) {
      reset({
        cardId: requisition.cardId || "",
        totalPayment: requisition.totalPayment || 0,
        payTo: requisition.payTo || "",
        bankName: requisition.bankName || "",
        bankAccountNumber: requisition.bankAccountNumber || "",
        description: requisition.description || "",
        items:
          requisition.items?.length > 0
            ? requisition.items.map((item) => ({
                description: item.description,
                quantity: item.quantity,
                unitPrice: item.unitPrice,
              }))
            : [],
        approvers:
          requisition.reviewers?.length === 2
            ? requisition.reviewers.map((reviewer) => ({
                id: reviewer.id,
                name: reviewer.name,
                email: reviewer.email,
                level: reviewer.level,
              }))
            : [
                { id: "", name: "", email: "", level: 1 },
                { id: "", name: "", email: "", level: 2 },
              ],
      });
    } else if (!isEditMode) {
      reset({
        cardId: initialCardId || "",
        totalPayment: 0,
        payTo: "",
        bankName: "",
        bankAccountNumber: "",
        description: "",
        items: [{ description: "", quantity: 1, unitPrice: 0 }],
        approvers: [
          { id: "", name: "", email: "", level: 1 },
          { id: "", name: "", email: "", level: 2 },
        ],
      });
    }
  }, [isEditMode, requisition, reset, initialCardId]);

  // Handle mutation errors - show once and reset
  useEffect(() => {
    if (isError && error) {
      const errorMessage =
        getErrorDetails(error).message ||
        `Failed to ${
          isEditMode ? "update" : "add"
        } requisition. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });

      // Reset mutation immediately to prevent repeated toasts
      if (createError) resetCreate();
      if (updateError) resetUpdate();
    }
  }, [
    isError,
    error,
    toast,
    isEditMode,
    createError,
    updateError,
    resetCreate,
    resetUpdate,
  ]);

  const onSubmit = async (data: RequisitionFormData) => {
    if (isSubmitting || isLoading) return;

    if (isEditMode && !isDirty) {
      toast.warning({
        title: "No Changes",
        message: "No changes were made to update.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        cardId: data.cardId,
        totalPayment: data.totalPayment,
        payTo: data.payTo,
        bankName: data.bankName,
        bankAccountNumber: data.bankAccountNumber,
        description: data.description,
        totalAmount,
        items: data.items.map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
        })),
        approvers: data.approvers.map((approver) => ({
          id: approver.id,
          name: approver.name,
          email: approver.email,
          level: approver.level,
        })),
      };

      if (isEditMode && requisition?.id) {
        await updateRequisition({
          id: requisition.id,
          ...payload,
        }).unwrap();
      } else {
        await createRequisition(payload).unwrap();
      }

      setShowSuccess(true);
      toast.success({
        title: "Success",
        message: `Requisition ${
          isEditMode ? "updated" : "created"
        } successfully`,
      });
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Create"} error:`, err);
      // Error handling is done in useEffect
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccess(false);
    reset();
    resetCreate();
    resetUpdate();
    onClose();
  };

  const handleAddItem = () => {
    appendItem({ description: "", quantity: 1, unitPrice: 0 });
  };

  const handleRemoveItem = (index: number) => {
    if (itemFields.length > 1) {
      removeItem(index);
    } else {
      toast.warning({
        title: "Cannot Remove",
        message: "At least one item is required.",
      });
    }
  };

  const handleApproverChange = (
    index: number,
    staffOption: StaffOption | null
  ) => {
    setValue(`approvers.${index}.id`, staffOption?.id ?? "", {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`approvers.${index}.name`, staffOption?.name ?? "", {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`approvers.${index}.email`, staffOption?.email ?? "", {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(`approvers.${index}.level`, index + 1, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseModal}
          heading=""
          message={`Requisition has been ${
            isEditMode ? "updated" : "created"
          } successfully.`}
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess
    ? ""
    : `${isEditMode ? "Edit" : "Add"} Requisition`;
  const submitButtonText = isEditMode
    ? "Update Requisition"
    : "+ Add Requisition";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={modalTitle}
      className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white max-h-[90vh] overflow-y-auto"
    >
      {showSuccess ? (
        renderSuccessView()
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Controller
                control={control}
                name="cardId"
                render={({ field }) => (
                  <AppInputField
                    label="Card Id"
                    type="text"
                    error={errors.cardId?.message}
                    {...field}
                    placeholder="Enter card ID"
                  />
                )}
              />

              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <AppInputField
                    label="Description"
                    type="text"
                    error={errors.description?.message}
                    {...field}
                    placeholder="Enter description"
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Controller
                control={control}
                name="totalPayment"
                render={({ field }) => (
                  <AppInputField
                    label="Total payment"
                    type="number"
                    error={errors.totalPayment?.message}
                    {...field}
                    onChange={(e) => field.onChange(e.target.valueAsNumber)}
                    placeholder="Enter total payment"
                  />
                )}
              />

              <Controller
                control={control}
                name="payTo"
                render={({ field }) => (
                  <AppInputField
                    label="Pay To"
                    type="text"
                    error={errors.payTo?.message}
                    {...field}
                    placeholder="Enter recipient name"
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Controller
                control={control}
                name="bankName"
                render={({ field }) => (
                  <AppInputField
                    label="Bank Name"
                    type="text"
                    error={errors.bankName?.message}
                    {...field}
                    placeholder="Enter bank name"
                  />
                )}
              />

              <Controller
                control={control}
                name="bankAccountNumber"
                render={({ field }) => (
                  <AppInputField
                    label="Bank Account Number"
                    type="text"
                    error={errors.bankAccountNumber?.message}
                    {...field}
                    placeholder="Enter account number"
                  />
                )}
              />
            </div>
          </div>

          {/* Items Section */}
          <div className="space-y-4">
            {errors.items?.message && (
              <p className="text-red-500 text-sm">{errors.items.message}</p>
            )}

            <div className="space-y-4">
              {itemFields.map((field, index) => (
                <div
                  key={field.id}
                  className="grid grid-cols-2 md:grid-cols-12 gap-4 items-start p-2 bg-gray-100 rounded-lg"
                >
                  <div className="col-span-2 md:col-span-5">
                    <Controller
                      control={control}
                      name={`items.${index}.description`}
                      render={({ field }) => (
                        <AppInputField
                          label="Description"
                          type="text"
                          error={errors.items?.[index]?.description?.message}
                          {...field}
                          className="bg-white border-gray-200 h-[40px]"
                          placeholder="Item description"
                        />
                      )}
                    />
                  </div>

                  <div className="col-span-1 md:col-span-3">
                    <Controller
                      control={control}
                      name={`items.${index}.quantity`}
                      render={({ field: { onChange, value, ...field } }) => (
                        <AppInputField
                          label="Quantity"
                          type="number"
                          error={errors.items?.[index]?.quantity?.message}
                          {...field}
                          value={value}
                          onChange={(e) => onChange(parseFloat(e.target.value))}
                          className="bg-white border-gray-200 h-[40px]"
                          placeholder="Qty"
                          min="1"
                        />
                      )}
                    />
                  </div>

                  <div className="col-span-1 md:col-span-3">
                    <Controller
                      control={control}
                      name={`items.${index}.unitPrice`}
                      render={({ field: { onChange, value, ...field } }) => (
                        <AppInputField
                          label="Unit Price"
                          type="number"
                          error={errors.items?.[index]?.unitPrice?.message}
                          {...field}
                          value={value}
                          onChange={(e) => onChange(parseFloat(e.target.value))}
                          className="bg-white border-gray-200 h-[40px]"
                          placeholder="Price"
                          min="1"
                        />
                      )}
                    />
                  </div>

                  <div className="col-span-2 md:col-span-1 flex items-end md:h-[72px]">
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(index)}
                      disabled={itemFields.length === 1}
                      className="w-full h-[48px] flex items-center justify-center text-red-500 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
                      title="Remove item"
                    >
                      <Trash2 />
                    </button>
                  </div>

                  {/* Item Total */}
                  <div className="col-span-12 text-right text-sm text-gray-600">
                    Subtotal: ₦
                    {(
                      (watchItems[index]?.quantity || 0) *
                      (watchItems[index]?.unitPrice || 0)
                    ).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center">
              <button
                type="button"
                onClick={handleAddItem}
                className="text-primary text-[13px] font-semibold"
              >
                + Add Item
              </button>
            </div>
          </div>

          {/* Approvers Section */}
          <div className="space-y-4">
            <h3 className="font-semibold">Approvers</h3>

            {errors.approvers?.message && (
              <p className="text-red-500 text-sm">{errors.approvers.message}</p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[0, 1].map((index) => (
                <div key={index} className="space-y-2">
                  <label className="text-sm font-medium">
                    Approver {index + 1}
                  </label>
                  <Controller
                    control={control}
                    name={`approvers.${index}.id`}
                    render={({ field }) => (
                      <div>
                        <StaffDropdown
                          value={field.value}
                          onChange={(option) => {
                            field.onChange(option?.id ?? "");
                            handleApproverChange(index, option);
                          }}
                          className="w-full bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                        />
                        {errors.approvers?.[index]?.id && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.approvers[index]?.id?.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center items-center space-x-4 text-[15px] pt-4">
            <AppButton
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px] disabled:opacity-50"
            >
              Cancel
            </AppButton>

            <AppButton
              type="submit"
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors disabled:opacity-50 text-[15px]"
            >
              {isSubmitting || isLoading ? <Spinner /> : submitButtonText}
            </AppButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default RequisitionModal;
